import { useEffect } from 'react';
import { Button, Form, Space, Tabs } from 'antd';
import { useCreate, useTranslate } from '@refinedev/core';
import { SaveButton } from '@refinedev/antd';
import { useWatch } from 'antd/es/form/Form';

import { useRulesCreateState } from 'pages/rules/context/RulesCreateStateContext';
import { initialValues } from 'pages/rules/RulesDummyData';
import useRulesDropdown from 'pages/rules/RulesDropdown';
import buildRuleTabs from 'pages/ruleChange/rule/buildRuleTabs';
import { getDmnOutputVariableList } from 'pages/rules/ruleApiResponse';
import { useRulesContext } from 'contexts/rulesContext/RulesContext';
// prettier-ignore
import {buildConfigFields,buildGroupConfigFields} from 'pages/ruleChange/rule/buildConfigFields';
import apiEndpoint from 'core/common/apiEndPoints';
import openNotificationWithIcon from 'core/utils/notification';
import constants from 'core/constants/Constants';
import usePermission from 'core/utils/usePermission';
import PrivateRouteConst from 'core/common/privateRouteConstant';
import DropdownInput from 'components/dropdown/dropDownInput';

// prettier-ignore
const RuleFormTabs = ({ form, formProps, saveButtonProps, onFinishHandler, unifiedListUnfiltered, ruleGroupDate, ruleType, saveLoading, type }) => {
  const t = useTranslate();
  const state = useRulesCreateState();
  const { fileType } = useRulesDropdown();
  const activeTab = useWatch('internalActiveTab', { form, preserve: true }) || 'details';
  const setActiveTab = (key: string) => form.setFieldValue('internalActiveTab', key);
  const tagsChecked = useWatch('merge_with_tag_name', { form, preserve: true });

  useEffect(() => {
    if (!form.getFieldValue('internalActiveTab')) {
      form.setFieldValue('internalActiveTab', 'details');
    }
  }, []);

  const checkPermission = usePermission();
  const hasGridView = checkPermission(PrivateRouteConst.GRID_VIEW);
  const hasApplicabilityView = checkPermission(PrivateRouteConst.RULE_APPLICABILITY_VIEW);
  const hasFormulaView = checkPermission(PrivateRouteConst.OUTPUT_FORMULA_VIEW);

  const uploadType = useWatch('file_upload_type', { form, preserve: true });
  const clawbackType = useWatch('clawback_type', { form, preserve: true });
  const adjustmentLevel = useWatch('adjustment_level', { form, preserve: true });
  const ruleGroupConfigurationStatus = useWatch(
    'rule_group_configuration_status',
    { form, preserve: true }
  );
  const ruleGroupId = useWatch('rule_group', { form, preserve: true });
  const gridType = useWatch('grid_type', { form, preserve: true });
  const hasShow = type === 'Show';
  const { rulePlanDataObj } = useRulesContext();
  const rulePlanId =
    type === 'Show' && rulePlanDataObj?.view_unique_id
      ? rulePlanDataObj.view_unique_id
      : rulePlanDataObj?.edit_unique_id || rulePlanDataObj?.unique_id;
  const { dmnOutputList } = getDmnOutputVariableList(ruleGroupId, rulePlanId, hasShow);

  const isFlatAmountMode = ruleGroupConfigurationStatus === false;

  const isClawbackDirect =
    ruleType === 'clawback' &&
    (clawbackType === 'RECALCULATION' || clawbackType === 'FULL');
  const isClawbackNotSelected = ruleType === 'clawback' && !clawbackType;
  const isClawbackDetailsOnly = isClawbackDirect || isClawbackNotSelected;

  // clawback PARTIAL → Details + Payout Matrix only (no Apply Rule, no Output Formula)
  const isClawbackPartial = ruleType === 'clawback' && clawbackType === 'PARTIAL';
  const skipAllOtherTabs = isFlatAmountMode || isClawbackDetailsOnly;

  const { mutate: sendForApprovalMutate, isLoading: sendForApprovalLoading } = useCreate();
  const ruleDraftId = form.getFieldValue('unique_id');
  const ruleVersionList = useWatch('rule_version_list', { form, preserve: true });
  const handleSendForApproval = () => {
    if (!ruleDraftId) return;
    sendForApprovalMutate(
      {
        dataProviderName: 'gridApprovalWorkflowDataProvider',
        resource: apiEndpoint.sendGridForApproval,
        values: { rule_draft_id: ruleDraftId },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (data) => {
          const successMessage =
            (data?.data as any)?.message || t('notifications.mutationSuccess');
          openNotificationWithIcon(constants.success, t('notifications.mutationSuccess'), successMessage);
        },
        onError: (error: any) => {
          const errorMessage = error?.message || t('notifications.mutationError');
          openNotificationWithIcon(constants.error, t('notifications.mutationError'), errorMessage);
        },
      }
    );
  };

  // Build state.configFields from unified list + DMN output columns (all rule types)
  useEffect(() => {
    const { concatList, configFields } = buildConfigFields({
      unifiedListUnfiltered,
      ruleType,
      dmnOutputList,
      dmnDataOutputFields: state.dmnData?.outputFields,
    });
    state.setConcatList(concatList);
    if (configFields?.length > 0) {
      state.setConfigFields(configFields);
    }
  }, [unifiedListUnfiltered, ruleType, state.dmnData?.outputFields, dmnOutputList]);

  const groupConfigFields = buildGroupConfigFields({
    dmnOutputList,
    dmnDataOutputFields: state.dmnData?.outputFields,
  });
  const outputFormulaConfigFields =
    adjustmentLevel === 'group' ? groupConfigFields : state.configFields;

  const isValuesPresent = formProps?.initialValues?.results || initialValues;

  const showFeelExpression = ruleType !== 'clawback' && hasFormulaView;
  const showRuleApplicability =
    ruleType !== 'clawback' && state.configFields && adjustmentLevel !== 'group' && hasApplicabilityView;

const tabItems = buildRuleTabs({
  t, form, type, ruleGroupDate, ruleType, state, fileType, uploadType,
  clawbackType, isFlatAmountMode, skipAllOtherTabs, isClawbackPartial,
  showRuleApplicability, showFeelExpression, adjustmentLevel,
  outputFormulaConfigFields, hasGridView, tagsChecked
}).map((item) => ({ ...item, forceRender: true }));

  const tabKeys = tabItems.map((tab) => tab.key);
  const currentIndex = tabKeys.indexOf(activeTab);
  const isFirstTab = currentIndex <= 0;
  const isLastTab = currentIndex === tabKeys.length - 1;

  const goNext = async () => {
    if (activeTab === 'details') {
      try {
        await form.validateFields();
      } catch {
        return; // antd shows the existing red field errors automatically
      }
    }

    if (activeTab === 'applyRule') {
      state.setIsSavedClick(true);
      const ruleApplicabilty = form.getFieldValue('rule_expression');
      const hasRuleApplicability =
        (ruleApplicabilty?.query?.children1?.length || 0) > 0;
      if (!hasRuleApplicability) {
        state.setRuleApplicabilityError(true);
        return;
      }
    }
    if (activeTab === 'outputFormula') {
      state.setIsSavedClick(true);
      const outputformula = form.getFieldValue('output_formula');
      const hasQuery =
        outputformula?.query && Object.keys(outputformula.query).length > 0;
      const isSimpleFormula = outputformula?.type && !hasQuery;
      const simpleFormulaFilled =
        isSimpleFormula &&
        Object.values(outputformula?.fields || {}).some(
          (v) => v !== '' && v !== null && v !== undefined
        );
      const advancedFormulaFilled =
        (outputformula?.query?.children1?.length || 0) > 0;
      const hasOutputFormula = simpleFormulaFilled || advancedFormulaFilled;

      if (!hasOutputFormula) {
        state.setOutputFormulaError(true);
        return;
      }
    }

    if (!isLastTab) setActiveTab(tabKeys[currentIndex + 1]);
  };

  const goPrevious = () => {
    if (!isFirstTab) setActiveTab(tabKeys[currentIndex - 1]);
  };

  const handleFormFinish = (values: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { internalActiveTab: unusedActiveTab, ...cleanValues } = values;
    if (skipAllOtherTabs || isClawbackPartial) {
      onFinishHandler(cleanValues);
      return;
    }

    const ruleApplicabilty = form.getFieldValue('rule_expression');
    const outputformula = form.getFieldValue('output_formula');

    const hasRuleApplicability = !!ruleApplicabilty?.query;

    const hasQuery =
      outputformula?.query && Object.keys(outputformula.query).length > 0;
    const isSimpleFormula = outputformula?.type && !hasQuery;
    const simpleFormulaFilled =
      isSimpleFormula &&
      Object.values(outputformula?.fields || {}).some(
        (v) => v !== '' && v !== null && v !== undefined
      );
    const advancedFormulaFilled = hasQuery;
    const hasOutputFormula = simpleFormulaFilled || advancedFormulaFilled;

    if (
      !cleanValues?.enter_amount &&
      showRuleApplicability &&
      !hasRuleApplicability
    ) {
      setActiveTab('applyRule');
      state.setRuleApplicabilityError(true);
      return;
    }
    if (!cleanValues?.enter_amount && showFeelExpression && !hasOutputFormula) {
      setActiveTab('outputFormula');
      state.setOutputFormulaError(true);
      return;
    }
    onFinishHandler(cleanValues);
  };

  const effectiveIsLastTab = skipAllOtherTabs ? true : isLastTab;
  const effectiveIsFirstTab = skipAllOtherTabs ? true : isFirstTab;

  return (
    <Form
      {...formProps}
      form={form}
      initialValues={{ ...isValuesPresent, internalActiveTab: 'details' }} onFinish={handleFormFinish}
      disabled={type === 'Show'}
      layout="vertical"
    >
      <Form.Item name="internalActiveTab" hidden>
        <input type="hidden" />
      </Form.Item>
      <Tabs
        items={tabItems}
        activeKey={activeTab}
        onChange={setActiveTab}
        tabBarExtraContent={
          <Space>
            {ruleVersionList?.length > 1 ? (
              <div className="rule-version-dropdown">
                <DropdownInput
                  label={t('rules.ruleVersion')}
                  name="rule_version"
                  size="small"
                  required={true}
                  optionList={ruleVersionList?.map((item: any) => ({
                    label: item,
                    value: item,
                  }))}
                  message={t('rules.messages.ruleVersion')}
                  placeholder={t('rules.messages.ruleVersion')}
                  disabled={false}
                />
              </div>
            ) : null}
            {activeTab === 'payoutMatrix' && gridType === 'custom' ? (
              <Button
                type="primary"
                loading={sendForApprovalLoading}
                onClick={handleSendForApproval}
              >
                {t('buttons.sendForApproval')}
              </Button>
            ) : null}
          </Space>
        }
      />
      {type !== 'Show' ? (
        <div className="flex justify-between mt-6">
          <Button onClick={goPrevious} disabled={effectiveIsFirstTab}>
            {t('buttons.back')}
          </Button>
          {effectiveIsLastTab ? (
            <SaveButton
              icon={false}
              {...saveButtonProps}
              loading={saveLoading}
              className="min-w-[100px]"
            >
              {t('buttons.saveRule')}
            </SaveButton>
          ) : (
            <Button type="primary" onClick={goNext}>
              {t('buttons.next')}
            </Button>
          )}
        </div>
      ) : null}
    </Form>
  );
};

export default RuleFormTabs;
